import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { useAuth, API } from "../context/AuthContext";
import { toast } from "sonner";
import { Lock, Plus, CreditCard, TrendingUp, Zap, CheckCircle2, Rocket, Calendar, Gamepad2 } from "lucide-react";

export default function OrganizerDashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [license, setLicense] = useState(null);
  const [rallies, setRallies] = useState([]);
  const [billing, setBilling] = useState({ payments: [], invoices: [], licenses: [] });
  const [newRally, setNewRally] = useState({ name: "", location: "", country: "Italia", start_date: "", end_date: "" });
  const [creating, setCreating] = useState(false);
  const [freeEvent, setFreeEvent] = useState(false);

  const activateFree = async () => {
    try {
      await axios.post(`${API}/organizer/license/free-event`);
      toast.success("Licenza Evento attivata gratis!");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    if (user.role === "SUPER_ADMIN") { nav("/admin/dashboard"); return; }
    load();
  }, [loading, user]);

  const load = async () => {
    axios.get(`${API}/settings/public`).then(s => setFreeEvent(!!s.data.free_event_license)).catch(() => {});
    const [p, l, r, b] = await Promise.all([
      axios.get(`${API}/organizer/profile`).catch(() => ({ data: null })),
      axios.get(`${API}/organizer/license`).catch(() => ({ data: null })),
      axios.get(`${API}/organizer/rallies`).catch(() => ({ data: [] })),
      axios.get(`${API}/billing/history`).catch(() => ({ data: { payments: [], invoices: [], licenses: [] }})),
    ]);
    if (!p.data) { nav("/onboarding"); return; }
    setProfile(p.data); setLicense(l.data); setRallies(r.data); setBilling(b.data);
  };

  const createRally = async () => {
    setCreating(true);
    try {
      await axios.post(`${API}/organizer/rallies`, { ...newRally, start_date: newRally.start_date || null, end_date: newRally.end_date || null });
      toast.success("Bozza rally creata");
      setNewRally({ name: "", location: "", country: "Italia", start_date: "", end_date: "" });
      load();
    } catch (e) { toast.error("Errore"); } finally { setCreating(false); }
  };

  const publish = async (r) => {
    try {
      await axios.post(`${API}/organizer/rallies/${r.id}/publish`);
      toast.success("Rally pubblicato nell'Official Paddock!");
      load();
    } catch (e) {
      if (e.response?.data?.detail === "ACTIVATE_RALLY_LOVE_PRO") {
        toast.error("Attiva Rally Love PRO per pubblicare");
        nav("/pricing");
      } else if (e.response?.data?.detail === "EVENT_LIMIT_REACHED") {
        toast.error("Limite eventi raggiunto — aggiorna il piano");
        nav("/pricing");
      } else {
        toast.error(e.response?.data?.detail || "Errore");
      }
    }
  };

  if (!profile) return null;

  const hasLicense = !!license;
  const revenue = billing.payments.filter(p => p.payment_status === "paid").reduce((s, p) => s + (p.amount || 0), 0);

  return (
    <div className="min-h-screen grain-bg">
      <Navbar variant="pro" />
      <section className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">

          {/* Header */}
          <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
            <div>
              <div className="pill-badge mb-2">DASHBOARD ORGANIZZATORE</div>
              <h1 className="font-display font-black text-3xl md:text-4xl" data-testid="org-dash-title">{profile.organizer_name}</h1>
              <p className="text-slate-400 text-sm">{profile.country} · {profile.company_name}</p>
            </div>
            <div className="flex gap-2">
              <button className="btn-outline-rl !py-2 !px-4 text-sm" onClick={() => nav("/pricing")} data-testid="upgrade-btn">AGGIORNA PIANO</button>
              {hasLicense && <button className="btn-outline-rl !py-2 !px-4 text-sm" onClick={() => nav("/pricing")} data-testid="renew-btn">RINNOVA</button>}
              <button className="btn-crimson !py-2 !px-4 text-sm" onClick={() => nav("/pricing")} data-testid="buy-event-btn">ACQUISTA LICENZA EVENTO</button>
            </div>
          </div>

          {/* License card */}
          {!hasLicense ? (
            <div className="rl-card p-8 mb-8 rl-glow-crimson border-[#FF1E27]/40" style={{ borderColor: "rgba(255,30,39,0.4)" }}>
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-lg bg-[#FF1E27]/15 flex items-center justify-center">
                    <Lock className="w-7 h-7 text-[#FF1E27]" />
                  </div>
                  <div>
                    <div className="font-display font-black text-2xl" data-testid="activate-pro-headline">ATTIVA RALLY LOVE PRO</div>
                    <div className="text-sm text-slate-300 max-w-lg">Il tuo rally è pronto. Attiva Rally Love PRO per pubblicare il tuo Official Paddock.</div>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {freeEvent && <button className="btn-crimson" onClick={activateFree} data-testid="activate-free-event-dash-btn">ATTIVA LICENZA EVENTO GRATIS</button>}
                  <button className="btn-outline-rl" onClick={() => nav("/pricing")} data-testid="choose-license-btn">SCEGLI LA TUA LICENZA</button>
                </div>
              </div>
            </div>
          ) : (
            <div className="rl-card p-6 mb-8 border-[#30D158]/30" style={{ borderColor: "rgba(48,209,88,0.3)" }}>
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-[#30D158]/15 flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-[#30D158]" />
                  </div>
                  <div>
                    <div className="pill-badge pill-amber mb-1">RALLY LOVE PRO · {license.plan_code}</div>
                    <div className="font-display font-bold text-lg" data-testid="license-active-text">LA TUA LICENZA RALLY LOVE PRO È ATTIVA</div>
                    <div className="text-xs font-mono-rl text-slate-400">SCADE: {new Date(license.expiration_date).toLocaleDateString()}</div>
                  </div>
                </div>
                <div className="font-mono-rl text-sm">
                  <div className="text-slate-400">EVENTI USATI</div>
                  <div className="text-2xl font-bold text-white">
                    {license.events_used}/{license.events_allowed === 0 ? "∞" : license.events_allowed}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Metrics */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Metric icon={Rocket} label="Rally" value={rallies.length} accent="text-[#FF1E27]" />
            <Metric icon={Zap} label="Pubblicati" value={rallies.filter(r => r.published).length} accent="text-[#FF9F0A]" />
            <Metric icon={CreditCard} label="Fatture" value={billing.invoices.length} accent="text-[#00F0FF]" />
            <Metric icon={TrendingUp} label="Speso" value={`€${revenue.toFixed(0)}`} accent="text-[#30D158]" />
          </div>

          {/* Rallies */}
          <div className="grid lg:grid-cols-3 gap-6 mb-8">
            <div className="lg:col-span-2 rl-card p-6">
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-display font-bold text-xl">Rally</h2>
                <div className="flex gap-2 flex-wrap"><button onClick={() => nav("/organizer/promax")} data-testid="organizer-promax-btn" className="btn-outline-rl !py-1.5 !px-3 text-xs border-[#FF9F0A]/50 text-[#FF9F0A]">APRI PROMAX</button><button onClick={() => nav("/promax")} data-testid="organizer-promax-info-btn" className="btn-outline-rl !py-1.5 !px-3 text-xs">COS'È PROMAX</button><button onClick={() => nav("/organizer/loyalty")} data-testid="organizer-loyalty-btn" className="btn-outline-rl !py-1.5 !px-3 text-xs">LOYALTY & DISCOUNTS</button></div>
                <span className="text-xs font-mono-rl text-slate-400 uppercase tracking-widest">{rallies.length} totali</span>
              </div>
              {rallies.length === 0 && <div className="text-sm text-slate-500 py-6 text-center">Nessun rally ancora. Crea il tuo primo.</div>}
              <div className="space-y-2">
                {rallies.map(r => (
                  <div key={r.id} className="flex items-center justify-between border border-white/5 rounded-lg px-4 py-3 hover:border-[#FF1E27]/30 transition"
                    data-testid={`rally-row-${r.id}`}>
                    <div>
                      <div className="font-semibold">{r.name}</div>
                      <div className="text-xs text-slate-500 font-mono-rl">{r.location || "—"} · {r.status}</div>
                    </div>
                    <div className="flex items-center gap-2">
                    <button onClick={() => nav(`/organizer/rallies/${r.id}/game`)} data-testid={`game-${r.id}-btn`}
                      className="btn-outline-rl !py-1.5 !px-3 text-xs flex items-center gap-1"><Gamepad2 className="w-3 h-3" /> GAME</button>
                    {r.published ? (
                      <span className="pill-badge pill-amber">PUBBLICATO</span>
                    ) : (
                      <button onClick={() => publish(r)} data-testid={`publish-${r.id}-btn`}
                        className="btn-outline-rl !py-1.5 !px-3 text-xs">Pubblica</button>
                    )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rl-card p-6">
              <h2 className="font-display font-bold text-xl mb-4 flex items-center gap-2"><Plus className="w-5 h-5 text-[#FF1E27]" /> Crea rally</h2>
              <div className="space-y-3">
                <input value={newRally.name} onChange={e => setNewRally({...newRally, name: e.target.value})}
                  placeholder="Nome rally" data-testid="new-rally-name"
                  className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm outline-none focus:border-[#FF1E27]" />
                <input value={newRally.location} onChange={e => setNewRally({...newRally, location: e.target.value})}
                  placeholder="Località" data-testid="new-rally-location"
                  className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm outline-none focus:border-[#FF1E27]" />
                <input value={newRally.country} onChange={e => setNewRally({...newRally, country: e.target.value})}
                  placeholder="Paese" data-testid="new-rally-country"
                  className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm outline-none focus:border-[#FF1E27]" />
                <div className="grid grid-cols-2 gap-2">
                  <div><label className="text-[10px] font-mono-rl text-slate-500 tracking-widest">INIZIO</label>
                    <input type="date" value={newRally.start_date} onChange={e => setNewRally({...newRally, start_date: e.target.value})} data-testid="new-rally-start"
                      className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm outline-none focus:border-[#FF1E27]" /></div>
                  <div><label className="text-[10px] font-mono-rl text-slate-500 tracking-widest">FINE</label>
                    <input type="date" value={newRally.end_date} onChange={e => setNewRally({...newRally, end_date: e.target.value})} data-testid="new-rally-end"
                      className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm outline-none focus:border-[#FF1E27]" /></div>
                </div>
                <button onClick={createRally} disabled={creating || !newRally.name}
                  data-testid="create-rally-btn"
                  className="btn-crimson w-full text-sm">
                  {creating ? "Creazione..." : "CREA BOZZA"}
                </button>
              </div>
            </div>
          </div>

          {/* Billing */}
          <div className="rl-card p-6">
            <h2 className="font-display font-bold text-xl mb-4">Pagamenti e storico licenze</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs font-mono-rl text-slate-500 uppercase tracking-widest border-b border-white/5">
                    <th className="py-2">Data</th><th>Piano</th><th>Stato</th><th className="text-right">Importo</th>
                  </tr>
                </thead>
                <tbody>
                  {billing.payments.length === 0 && (
                    <tr><td colSpan="4" className="py-6 text-center text-slate-500">Nessuna transazione ancora.</td></tr>
                  )}
                  {billing.payments.map((p, i) => (
                    <tr key={i} className="border-b border-white/5">
                      <td className="py-3 text-slate-300 font-mono-rl text-xs">{new Date(p.created_at).toLocaleDateString()}</td>
                      <td>{p.plan_code}</td>
                      <td><span className={`text-xs font-mono-rl ${p.payment_status === "paid" ? "text-[#30D158]" : "text-slate-500"}`}>{p.payment_status.toUpperCase()}</span></td>
                      <td className="text-right font-mono-rl">€{(p.amount || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </section>
    </div>
  );
}

const Metric = ({ icon: Icon, label, value, accent }) => (
  <div className="rl-card p-5">
    <div className="flex items-center gap-2 text-xs font-mono-rl tracking-widest text-slate-400 uppercase mb-1">
      <Icon className={`w-4 h-4 ${accent}`} /> {label}
    </div>
    <div className="font-display font-black text-3xl">{value}</div>
  </div>
);
